import round from '@/trials/lib/round';

/**
 * highchartの棒グラフでヒストグラムを表現するためのデータに変換します。
 * @param infoList ヒストグラム生成の元となるデータのリスト
 */
export default function toHistogram(infoList: any[]): { [key: string]: any } {
    /**
     * 全ての数値データを保持
     */
    let allData: number[] = [];

    /**
     * 全てのデータが整数かどうか
     */
    let isAllInteger = true;

    /**
     * 出現した数値を保持
     */
    const uniqueValues: number[] = [];

    /**
     * infoList から、 allData, isAllInteger, uniqueValues を設定
     */
    for (let i = 0; i < infoList.length; i++) {
        const info = infoList[i];
        allData = allData.concat(info.data);
        for (let j = 0; j < info.data.length; j++) {
            const val = info.data[j];
            if (isAllInteger && !Number.isInteger(val)) {
                isAllInteger = false;
            }
            if (!uniqueValues.includes(val)) {
                uniqueValues.push(val);
            }
        }
    }

    /**
     * 数値データの中で最大、最小を保持
     */
    const minValue = Math.min.apply(null, allData);
    const maxValue = Math.max.apply(null, allData);

    /**
     * 全て同じ値の場合は返り値をここで生成して終了
     */
    if (maxValue === minValue) {
        const categories = [maxValue];
        const series: any[] = [];
        for (let i = 0; i < infoList.length; i++) {
            const info = infoList[i];
            series.push({
                name: info.name,
                data: [info.data.length],
            });
        }

        return {
            series: series,
            categories: categories,
        };
    }

    /**
     * binsの数、幅を計算
     * 基本的に下記閾値の数に収まるようにしています。
     */
    const binsCountThreshold = 10;

    /**
     * binsの数
     * 平方根で小数点以下切り捨て
     */
    let binsCount = Math.min(binsCountThreshold, Math.floor(Math.sqrt(allData.length)));

    /**
     * binsの幅
     */
    let binWidth = (maxValue - minValue) / binsCount;

    /**
     * 閾値より数値のバリエーションが少ない場合、
     * 数値のバリエーション分の戻り値を生成して終了
     */
    if (binsCountThreshold >= uniqueValues.length) {
        const values = [...uniqueValues];
        values.sort();

        const thresholds: any[] = [];

        /**
         * 1つの数値が1つのbinに収まるようなthresholdsを返して終了
         * 例；begin: 5, end: 5
         */
        for (let i = 0; i < values.length; i++) {
            const begin = values[i];
            thresholds.push({
                begin: begin,
                end: begin,
            });
        }

        binsCount = uniqueValues.length;

        return buildHistogramResponse(binsCount, thresholds, infoList);
    }

    /**
     * 各 bin の具体的な閾値の生成
     */
    const thresholds: any[] = createThresholds(
        binsCount,
        binWidth,
        minValue,
        maxValue,
        isAllInteger
    );

    /**
     * 具体的な閾値の数が最終的なbinsCountになる
     */
    binsCount = thresholds.length;

    return buildHistogramResponse(binsCount, thresholds, infoList);
}

/**
 * ヒストグラム用データを構築します
 * @param binsCount bin数
 * @param thresholds 閾値リスト
 * @param infoList 元データ
 */
function buildHistogramResponse(binsCount: number, thresholds: any[], infoList: any[]): any {
    /**
     * カテゴリ名生成
     */
    const categories: string[] = [];
    for (let i = 0; i < binsCount; i++) {
        const begin = round(thresholds[i].begin, 2);
        const end = round(thresholds[i].end, 2);

        if (begin === end) {
            // 範囲がない場合、categoryのテキストも範囲表記にしない
            categories.push(`${begin}`);
        } else {
            categories.push(`${begin} - ${end}`);
        }
    }

    /**
     * seriesを作成
     */
    const series: any[] = [];
    for (let i = 0; i < infoList.length; i++) {
        const info = infoList[i];
        const data: any[] = Array(binsCount).fill(0);

        for (let j = 0; j < info.data.length; j++) {
            const val = info.data[j];
            let index = 0;

            // 数値がどのbinに属するか
            for (let k = 0; k < binsCount; k++) {
                const threshold = thresholds[k];
                if (val <= threshold.end) {
                    index = k;
                    break;
                }
            }
            data[index]++;
        }
        series.push({
            name: info.name,
            data: data,
        });
    }

    return {
        series: series,
        categories: categories,
    };
}

/**
 * 閾値リストを生成
 * @param binsCount bin数
 * @param binWidth bin幅
 * @param minValue 最小値
 * @param maxValue 最大値
 * @param isAllInteger 全て整数かどうか
 */
function createThresholds(
    binsCount: number,
    binWidth: number,
    minValue: number,
    maxValue: number,
    isAllInteger: boolean
): any[] {
    let thresholds: any[] = [];

    /**
     * 整数ばかりのデータ or 実数含めるデータで threshold の作り方を切り替える
     */
    if (isAllInteger) {
        thresholds = createThresholdsForInt(binsCount, binWidth, minValue, maxValue);
    } else {
        thresholds = createThresholdsForFloat(binsCount, binWidth, minValue, maxValue);
    }

    /**
     * 最初と最後の閾値だけ再調整
     */
    const lastIndex = thresholds.length - 1;
    thresholds[0].begin = minValue;
    thresholds[lastIndex].end = maxValue;

    /**
     * 最後の閾値の begin と end が同じになるケースがあるので、ひとつ前の閾値に含める
     */
    if (thresholds[lastIndex].begin === thresholds[lastIndex].end) {
        const lastThreshold: any = thresholds.pop();
        thresholds[lastIndex - 1].end = maxValue;
    }

    return thresholds;
}

/**
 * 整数データ用の閾値リスト生成
 * @param binsCount bin数
 * @param binWidth bin幅
 * @param minValue 最小値
 * @param maxValue 最大値
 */
function createThresholdsForInt(
    binsCount: number,
    binWidth: number,
    minValue: number,
    maxValue: number
): any[] {
    const thresholds: any[] = [];

    let begin = minValue;
    for (let i = 0; i < binsCount; i++) {
        const end = round(begin + binWidth, 2);

        thresholds.push({
            begin: begin,
            end: end,
        });

        if (end > maxValue) {
            break;
        }

        // 次の開始数
        begin = end + 1;
    }

    // もしすべてが整数であれば、閾値も整数にする
    for (let i = 0; i < thresholds.length; i++) {
        thresholds[i].begin = round(thresholds[i].begin, 0);
        thresholds[i].end = round(thresholds[i].end, 0);
    }

    return thresholds;
}

/**
 * 実数データ用の閾値リスト生成
 * @param binsCount bin数
 * @param binWidth bin幅
 * @param minValue 最小値
 * @param maxValue 最大値
 */
function createThresholdsForFloat(
    binsCount: number,
    binWidth: number,
    minValue: number,
    maxValue: number
): any[] {
    const thresholds: any[] = [];
    let begin = minValue;
    for (let i = 0; i < binsCount; i++) {
        const end = round(begin + binWidth, 2);

        thresholds.push({
            begin: begin,
            end: end,
        });

        if (end > maxValue) {
            break;
        }

        // 次の開始数
        begin = end + 0.01;
    }
    return thresholds;
}
